/*eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useMediaQuery } from 'react-responsive';
import { DayPicker } from 'react-day-picker';
import BusModifySearchCollapsed from './BusModifySearchCollapsed';
import AutoComplete from './BusAutoComplete';

import { getAppEnv } from '../../../config/env';
import { format } from 'date-fns';
import { Context } from '../../../src/AppContext';
import { ifAdsPresent } from '../../utils/adsUtils';
import { generateUuid } from '../../utils/uuidGenerator';
import { yieldToMainThread } from '../../utils/scheduler';

import 'react-day-picker/dist/style.css';
import './BusModifySearch.css';
import './dayPickerBusReset.css';

const env = getAppEnv();
const { PUBLIC_URL } = env.raw;
const isSSR = () => typeof window === 'undefined';

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type BusSearchWidgetProps = {
  funnelUrl?: string;
  sourceCityName?: string;
  operatorName?: string;
  destinationCityName?: string;
  category?: string;
  widget: number | null;
  setWidget: React.Dispatch<React.SetStateAction<number | null>>;
  onFromChange: (city: string, code: string) => void;
  onToChange: (city: string, code: string) => void;
  fromCity: string;
  toCity: string;
  fromCode: string;
  toCode: string;
  setcollapsableFlag: React.Dispatch<React.SetStateAction<boolean>>;
  collapsableFlag: boolean;
  topFix?: string;
  isMobileDevice: boolean;
  demoLink: boolean;
};
const BusSearchWidget = ({
  funnelUrl,
  sourceCityName = '',
  operatorName = '',
  destinationCityName = '',
  category = '',
  widget,
  setWidget,
  onFromChange,
  onToChange,
  fromCity,
  toCity,
  fromCode,
  toCode,
  setcollapsableFlag,
  collapsableFlag,
  topFix,
  isMobileDevice,
  demoLink,
}: BusSearchWidgetProps) => {
  const { trackTemplateEvent, selectedDay, setSelectedDay } =
    useContext(Context);
  let { omniUrlArgs } = useContext(Context);
  const [didMount, setDidMount] = useState(false);
  const [fromFieldError, setFromFieldError] = useState(false);
  const [toFieldError, setToFieldError] = useState(false);
  const [inputName, setInputName] = useState('fromCity');
  const [calendarSource, setCalendarSource] = useState<string | null>(null);

  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const onToggleClicked = () => {
    onFromChange(toCity, toCode);
    onToChange(fromCity, fromCode);
  };

  const handlePopup = (
    idx: number | null,
    cName: string,
    event:
      | React.FocusEvent<HTMLInputElement>
      | React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        if (cName) {
          setInputName(cName);
        }
        setWidget(idx);
      });
    });
  };

  const closePopup = () => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        setWidget(null);
      });
    });
  };

  const escFunction = (event) => {
    if (event.keyCode === 27) {
      setWidget(null);
    }
  };

  const isMobile = useMediaQuery({
    query: '(max-device-width: 768px)',
  });

  const getEventName = (
    operatorName: string,
    destinationCityName: string,
    category: string,
  ): string => {
    if (operatorName && destinationCityName) {
      // for route operator
      return 'Bus_SEO_Route_Operator_Date_Select';
    } else if (operatorName) {
      //for operator
      return 'Bus_SEO_Operator_Date_Select';
    } else if (destinationCityName && category) {
      //for route category
      return 'Bus_Seo_Route_Category_Date_Select';
    } else if (destinationCityName) {
      //for route
      return 'Bus_Seo_Route_Date_Select';
    } else {
      //for city
      return 'Bus_Seo_Date_Select';
    }
  };

  const handleDaySelect = (day?: Date) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        if (!day) return;
        setInputName('fromCity');
        setWidget(null);
        if (trackTemplateEvent) {
          const eventName = getEventName(
            operatorName,
            destinationCityName,
            category,
          );
          trackTemplateEvent(eventName);
        }
        setSelectedDay(day);
      });
    });
  };

  const openCal = () => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        setInputName('travelDate');
        setCalendarSource('travelDate');
        setWidget(2);
      });
    });
  };

  const getFilterSessionId = async () => {
    if (operatorName && operatorName !== fromCity && operatorName !== toCity) {
      const filterOption = {
        tag: 'Operator',
        value: operatorName,
      };

      const requestBody = {
        type: 'UserSessionSelectionRequest',
        seo: {
          pageName: 'Operator',
          filters: [filterOption],
        },
      };

      const sessionId = generateUuid();
      const headers = {
        sessionId,
        'mmt-source': 'SEO',
      };

      const endPoint = 'https://bus.makemytrip.com/bus/postFilterContext';
      try {
        const response = await axios.post(endPoint, requestBody, { headers });
      } catch (error) {
        // Handle the error
        console.error(error);
      }

      return sessionId;
    }
    return null;
  };

  const transferToFunnel = async (fromSearch = false) => {
    if (!fromCity) {
      setFromFieldError(true);

      setTimeout(() => {
        setFromFieldError(false);
      }, 2000);
      return;
    }

    if (!toCity) {
      setToFieldError(true);

      setTimeout(() => {
        setToFieldError(false);
      }, 2000);
      return;
    }

    if (ifAdsPresent()) {
      omniUrlArgs = omniUrlArgs.replace('NoAds', 'Ads');
    }
    const travelDate = format(selectedDay || new Date(), 'dd-MM-yyyy');
    if (funnelUrl && !fromSearch) {
      const Url = funnelUrl
        .replace(/deptDate/g, travelDate)
        .replace('mbus.makemytrip.com', 'www.makemytrip.com/mbus')
        .replace('bus.makemytrip.com', 'www.makemytrip.com');

      if (trackTemplateEvent) {
        if (operatorName && destinationCityName) {
          // for route operator
          trackTemplateEvent(
            'Bus_SEO_Route_Operator_Search_Click',
            fromCity,
            toCity,
          );
        }
        if (operatorName) {
          //for operator
          trackTemplateEvent('Bus_SEO_Operator_Search_Click', fromCity, toCity);
        } else if (destinationCityName && category) {
          //for route category
          trackTemplateEvent(
            `Bus_Seo_Route_Category_Search_Click`,
            fromCity,
            toCity,
          );
        } else if (destinationCityName) {
          //for route
          trackTemplateEvent(`Bus_Seo_Route_Search_Click`, fromCity, toCity);
        } else {
          //for city
          trackTemplateEvent(`Bus_Seo_Search_Click`, fromCity, toCity);
        }
      }
      if (!isMobileDevice && !operatorName) {
        const newTab = window.open(
          Object(Url.concat(`&${omniUrlArgs}`)),
          '_blank',
        );
        return newTab?.focus();
      } else {
        return (window.location.href = Object(Url.concat(`&${omniUrlArgs}`)));
      }
    } else {
      const UrlPrefix =
        isMobileDevice ||
        (typeof window !== 'undefined' && window?.innerWidth <= 768)
          ? `https://www.makemytrip.com/mbus/rnw/busListingPage?from=${fromCity}&from_code=${fromCode}&to=${toCity}&to_code=${toCode}&departure=${travelDate}&sort=Rating&`
          : `https://www.makemytrip.com/bus/search/${fromCity}/${toCity}/${travelDate}?from_city=${fromCode}&to_city=${toCode}&`;
      let urlSuffix = `${omniUrlArgs}`;

      const operatorFilterSessionId = await getFilterSessionId();

      if (operatorFilterSessionId) {
        urlSuffix = `sessionId=${operatorFilterSessionId}&${omniUrlArgs}`;
      }

      if (trackTemplateEvent) {
        if (operatorName && destinationCityName) {
          // for route operator
          trackTemplateEvent(
            'Bus_SEO_Route_Operator_Search_Click',
            fromCity,
            toCity,
          );
        }
        if (operatorName) {
          //for operator
          trackTemplateEvent('Bus_SEO_Operator_Search_Click', fromCity, toCity);
        } else if (destinationCityName && category) {
          //for route category
          trackTemplateEvent(
            `Bus_Seo_Route_Category_Search_Click`,
            fromCity,
            toCity,
          );
        } else if (destinationCityName) {
          //for route
          trackTemplateEvent(`Bus_Seo_Route_Search_Click`, fromCity, toCity);
        } else {
          //for city
          trackTemplateEvent(`Bus_Seo_Search_Click`, fromCity, toCity);
        }
      }
      const Url = UrlPrefix + urlSuffix;
      if (!isMobile) {
        const newTab = window.open(
          Object(Url.concat(`&${omniUrlArgs}`)),
          '_blank',
        );
        return newTab?.focus();
      } else {
        return (window.location.href = Object(Url.concat(`&${omniUrlArgs}`)));
      }
    }
  };

  const yieldTransferToFunnel = async (fromSearch = false) => {
    await yieldToMainThread(() => {
      requestAnimationFrame(() => {
        transferToFunnel(fromSearch);
      });
    });
  };

  useEffect(() => {
    if (!demoLink) {
      const body = document.getElementsByTagName('body');
      if (isMobileDevice) {
        body[0].classList.add('bodyNotOverflow');
      } else {
        body[0].classList.remove('bodyNotOverflow');
      }
    }
    document.addEventListener('keydown', escFunction, false);

    return () => {
      const body = document.getElementsByTagName('body');
      body[0].classList.remove('bodyNotOverflow');
      document.removeEventListener('keydown', escFunction, false);
    };
  });

  useEffect(() => {
    setDidMount(true);
    openCal();
  }, []);

  useEffect(() => {
    if (didMount) {
      yieldTransferToFunnel(calendarSource === 'travelDate');
      setCalendarSource(null);
    }
  }, [selectedDay]);

  const renderMobileHeader = () => {
    return (
      <BusModifySearchCollapsed
        setcollapsableFlag={setcollapsableFlag}
        sourceCityName={sourceCityName}
        operatorName={operatorName}
        destinationCityName={destinationCityName}
      />
    );
  };

  const renderDesktopHeader = () => {
    const arr = [
      <React.Fragment key={0}>
        <AutoComplete
          cityCode={fromCode}
          placeholder={'Where are you starting from?'}
          closeCityPopup={closePopup}
          onChange={onFromChange}
        />
        <div className="hsBackDrop" onClick={closePopup}></div>
      </React.Fragment>,
      <React.Fragment key={1}>
        <AutoComplete
          cityCode={toCode}
          placeholder={'To'}
          closeCityPopup={closePopup}
          onChange={onToChange}
        />
        <div className="hsBackDrop" onClick={closePopup}></div>
      </React.Fragment>,
      <React.Fragment key={2}>
        <div className="dayPickerBusWrap">
          {!isMobileDevice && (
            <div className="doubleDaypickerHeader">
              <span className="widgetSprite icCalendar appendRight10"></span>
              <span
                className="selectedDateField appendBottom8 latoBold font16 blacktext"
                suppressHydrationWarning={true}
              >
                {selectedDay && selectedDay.getDate() + ' '}
                {selectedDay && months[selectedDay.getMonth()] + ' '}
                {selectedDay && selectedDay.getFullYear() % 2000}
                {!selectedDay && 'Select Travel Date'}
              </span>
            </div>
          )}
          {isClient && (
            <DayPicker
              mode="single"
              selected={selectedDay || undefined}
              onSelect={handleDaySelect}
              numberOfMonths={isMobileDevice ? 1 : 2}
              defaultMonth={selectedDay || undefined}
              disabled={{
                before: (() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return today;
                })(),
              }}
            />
          )}
        </div>
        <div className="hsBackDrop" onClick={closePopup}></div>
      </React.Fragment>,
    ];
    return (
      <React.Fragment>
        <div
          className={`bsw ${widget !== null ? 'widgetOpen' : ''} ${
            topFix ? 'widgetFix' : ''
          }`}
          id={isMobileDevice ? `busModifySearchMobile` : `busModifySearch`}
        >
          {isMobileDevice && (
            <div className="makeFlex ">
              <div className="logoBanner">
                <img
                  data-src={`${PUBLIC_URL}/static/public/logo2.png`}
                  className="lazyload"
                  height={'20px'}
                  alt="logo"
                />
              </div>
              <div
                className="makeFlex"
                onClick={() => setcollapsableFlag(true)}
              >
                <div
                  style={{
                    color: '#fdfeff',
                    fontSize: '12px',
                    paddingTop: '2px',
                  }}
                >
                  Close
                </div>
                <div style={{ color: '#f2f2f2' }} className="paddingL7 font18">
                  &#10005;
                </div>
              </div>
            </div>
          )}
          <div className="bsw_inner">
            <div
              className={`bsw_inputBox selectHtlCity ${
                widget !== null ? `inactiveWidget` : ``
              } ${widget === 0 ? `activeWidget` : ``}`}
            >
              <label
                htmlFor="fromCity"
                className="lbl_input makeFlex column latoBold"
              >
                <span className="appendBottom5 font12 capText detailsHeadingText">
                  From
                </span>
                {fromCity === '' ? (
                  <div
                    id="fromCity"
                    className="placeholder"
                    onClick={(event) => handlePopup(0, 'fromCity', event)}
                  >
                    {isMobileDevice
                      ? `Enter From Location`
                      : `Select From City`}
                  </div>
                ) : (
                  <input
                    id="fromCity"
                    type="text"
                    className={`bsw_inputField truncate font16 whiteText ${
                      isMobileDevice ? 'latoBold' : ''
                    }`}
                    value={`${fromCity}`}
                    onFocus={(event) => handlePopup(0, 'fromCity', event)}
                    readOnly
                  />
                )}
              </label>
              <p
                className="dateErrorMsg whiteText"
                style={{ display: fromFieldError ? 'block' : 'none' }}
              >
                Please enter city location
              </p>
              {widget === 0 && arr[widget]}
            </div>
            <div
              className={`swap-icon marR8 desktopShow`}
              onClick={onToggleClicked}
            ></div>
            <div
              className={`bsw_inputBox selectHtlCity ${
                widget !== null ? `inactiveWidget` : ``
              } ${widget === 1 ? `activeWidget` : ``}`}
            >
              <label
                htmlFor="toCity"
                className="lbl_input latoBold makeFlex column"
              >
                <span className="appendBottom5 font12 capText detailsHeadingText">
                  To
                </span>
                {toCity === '' ? (
                  <div
                    id="toCity"
                    className="placeholder"
                    onClick={(event) => handlePopup(1, 'toCity', event)}
                  >
                    {isMobileDevice ? `Enter To Location` : `Select To City`}
                  </div>
                ) : (
                  <input
                    id="toCity"
                    type="text"
                    className={`bsw_inputField truncate font16 whiteText ${
                      isMobileDevice ? 'latoBold' : ''
                    } `}
                    value={`${toCity}`}
                    onFocus={(event) => handlePopup(1, 'toCity', event)}
                    readOnly
                  />
                )}
              </label>
              <p
                className="dateErrorMsg whiteText"
                style={{ display: toFieldError ? 'block' : 'none' }}
              >
                Please enter city location
              </p>
              {widget === 1 && arr[widget]}
            </div>
            <div
              className={`bsw_inputBox dayPickerRailWrap dates ${
                widget !== null ? `inactiveWidget` : ``
              }`}
            >
              <label
                htmlFor="travelDate"
                className="lbl_input latoBold makeFlex column"
              >
                <span className="appendBottom8 font12 capText detailsHeadingText">
                  DATE
                </span>
                <input
                  id="travelDate"
                  type="text"
                  className={`bsw_inputField truncate font16 whiteText ${
                    isMobileDevice ? 'latoBold' : ''
                  }`}
                  suppressHydrationWarning={true}
                  value={
                    selectedDay && !isSSR()
                      ? selectedDay.toDateString()
                      : 'Select Travel Date'
                  }
                  onFocus={
                    isMobileDevice
                      ? () => openCal()
                      : (event) => handlePopup(2, 'travelDate', event)
                  }
                  readOnly
                />
                {selectedDay ? (
                  <p className="font16 sCapText whiteText">
                    <span className="code latoRegular">
                      {days[selectedDay.getDay()]},&nbsp;
                    </span>
                    <span className="code paddingTB2">
                      <span className="latoBlack">{selectedDay.getDate()}</span>
                      <span className="latoRegular">
                        {' '}
                        {months[selectedDay.getMonth()]}
                      </span>
                      <span className="latoRegular shortYear">
                        {' '}
                        {selectedDay.getFullYear() % 2000}{' '}
                      </span>
                    </span>
                  </p>
                ) : (
                  <p className="font16 sCapText whiteText truncate">
                    Select Travel Date
                  </p>
                )}
              </label>
              {widget === 3 && arr[widget]}
              {widget === 2 && arr[widget]}
            </div>
            <div className="widgetSearchBtn--wrap">
              <button
                className="primaryBtn font18 latoBlack widgetSearchBtn"
                onClick={() => yieldTransferToFunnel(true)}
              >
                {' '}
                Search
              </button>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  };

  return collapsableFlag && isMobileDevice
    ? renderMobileHeader()
    : renderDesktopHeader();
};

export default BusSearchWidget;
